import { EyeOutlined, LogoutOutlined } from '@ant-design/icons';
import { Button, message, Space, Typography } from 'antd';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';

const { Text } = Typography;

/**
 * Állandó, el nem rejthető figyelmeztető sáv a felület tetején.
 *
 * A maszkolás jogosultság-eszkaláció: ha az admin elfelejti, hogy más nevében
 * jár, egy „ártalmatlan" kattintás valaki más adatait mozdítja el. Ezért a sáv
 * `position: fixed`, nincs bezáró gombja, és a `has-masquerade` osztályon
 * keresztül lejjebb tolja a teljes oldalt (App.css), hogy semmit ne takarjon.
 */
const MasqueradeBanner: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, stopMasquerade } = useAuthStore();
  const [stopping, setStopping] = useState(false);
  const masquerade = user?.masquerade;

  useEffect(() => {
    document.body.classList.toggle('has-masquerade', Boolean(masquerade));
    return () => document.body.classList.remove('has-masquerade');
  }, [masquerade]);

  if (!(masquerade && user)) {
    return null;
  }

  const impersonated = `${user.firstName} ${user.lastName}`.trim() || user.email;
  const original =
    `${masquerade.originalFirstName} ${masquerade.originalLastName}`.trim() ||
    masquerade.originalEmail;

  const handleStop = async () => {
    setStopping(true);
    try {
      await stopMasquerade();
      message.success(t('masquerade.stopped'));
      navigate('/');
    } catch {
      message.error(t('masquerade.stop_failed'));
    } finally {
      setStopping(false);
    }
  };

  return (
    <div className="masquerade-banner" role="alert">
      <Space size={10} wrap>
        <EyeOutlined aria-hidden />
        <Text strong style={{ color: 'inherit' }}>
          {t('masquerade.banner', { impersonated })}
        </Text>
        <Text style={{ color: 'inherit', opacity: 0.85 }}>
          {t('masquerade.banner_original', { original })}
        </Text>
        <Text style={{ color: 'inherit', opacity: 0.85 }}>
          {t('masquerade.expires_at', {
            time: new Date(masquerade.expiresAt).toLocaleTimeString(i18n.language, {
              hour: '2-digit',
              minute: '2-digit',
            }),
          })}
        </Text>
      </Space>
      <Button size="small" icon={<LogoutOutlined />} loading={stopping} onClick={handleStop}>
        {t('masquerade.stop')}
      </Button>
    </div>
  );
};

export default MasqueradeBanner;
